import type { NavBadgeValue, NavSection } from '@workspace/seedforge-ui/components/app-sidebar'
import {
  BarChart3,
  Bot,
  Brain,
  Building2,
  Database,
  Eye,
  FileText,
  Flag,
  FolderOpen,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  Mail,
  MailPlus,
  MessageCircle,
  MessagesSquare,
  Network,
  Send,
  ShieldBan,
  ShieldCheck,
  Sparkles,
  Users,
  Wifi,
} from 'lucide-react'
import type { AdminPendingCounts } from '../hooks/use-admin-pending-counts'

/**
 * Admin work-queue counts. These are internal backlog, unlike the founder nav
 * where a badge has to be something the founder can clear.
 */
function countBadge(count: number | undefined): NavBadgeValue | undefined {
  return count != null && count > 0 ? { text: String(count), tone: 'primary' } : undefined
}

export function buildAdminNavSections(counts: AdminPendingCounts = {}): NavSection[] {
  return [
    {
      title: '',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', to: '/admin' },
        { icon: BarChart3, label: 'Evaluations', to: '/admin/evaluations' },
      ],
    },
    {
      title: 'Platform',
      items: [
        { icon: Users, label: 'Users', to: '/admin/users' },
        { icon: Building2, label: 'Startups', to: '/admin/startups' },
      ],
    },
    {
      title: 'Investors',
      items: [
        { icon: Landmark, label: 'Investors', to: '/admin/investors' },
        { icon: ShieldCheck, label: 'Investor Checks', to: '/admin/investor-checks' },
        { icon: Bot, label: 'AI sessions', to: '/admin/agent' },
        { icon: Eye, label: 'Observers', to: '/admin/agent/observers' },
        {
          icon: Brain,
          label: 'Persona Research',
          to: '/admin/investors/persona-research',
          badge: countBadge(counts.personaResearch),
        },
        {
          icon: Sparkles,
          label: 'Matcher Jobs',
          to: '/admin/matcher-jobs',
          badge: countBadge(counts.matcherJobs),
        },
        {
          icon: Wifi,
          label: 'Activity Research',
          to: '/admin/investors/activity-research',
          badge: countBadge(counts.activityResearch),
        },
        {
          icon: Network,
          label: 'Portfolio Research',
          to: '/admin/investors/portfolio-research',
        },
        {
          icon: ShieldBan,
          label: 'Update Proposals',
          to: '/admin/investors/update-proposals',
          badge: countBadge(counts.investorUpdateProposals),
        },
      ],
    },
    {
      title: 'Contacts',
      items: [
        {
          icon: Send,
          label: 'Outreach',
          to: '/admin/outreach',
          badge: countBadge(counts.outreach),
        },
        {
          icon: MessagesSquare,
          label: 'Reply Classification',
          to: '/admin/outreach/reply-classification',
          badge: countBadge(counts.replyClassification),
        },
      ],
    },
    {
      title: 'Engagement',
      items: [
        { icon: FolderOpen, label: 'Data Rooms', to: '/admin/data-rooms' },
        { icon: MessagesSquare, label: 'Profile Chats', to: '/admin/profile-chats' },
        { icon: MessageCircle, label: 'Feedback', to: '/admin/feedback' },
        { icon: Mail, label: 'Emails', to: '/admin/emails' },
        { icon: MailPlus, label: 'Email Templates', to: '/admin/email-templates' },
        { icon: ListChecks, label: 'Sign-up Question', to: '/admin/signup-question' },
      ],
    },
    {
      title: 'Website',
      items: [
        { icon: FileText, label: 'Blog', to: '/admin/blog' },
        { icon: Database, label: 'Investor Index', to: '/admin/investor-index' },
        { icon: LifeBuoy, label: 'Help Center', to: '/admin/help-center' },
      ],
    },
    {
      title: 'System',
      items: [{ icon: Flag, label: 'Feature Flags', to: '/admin/feature-flags' }],
    },
  ]
}

export const ADMIN_NAV_SECTIONS: NavSection[] = buildAdminNavSections()
